import { getOperationCategoryLabel } from '../../../../common/operations/enums/OperationCategory';
import { getOperationContextLabel } from '../../../../common/operations/enums/OperationContext';
import { getOperationTypeLabel } from '../../../../common/operations/enums/OperationType';
import { GQLIdEncoder } from '../../../../common/utils/GQLIdEncoder';
import { CsvPayload } from '../../common/models/CsvPayload';
import { InstanceSummary } from '../models/InstanceCsvData';

export class InstanceCsvMapper {
  private readonly gqlIdEncoder = new GQLIdEncoder();

  mapGQLModel(data: any): InstanceSummary {
    return new InstanceSummary(
      this.mapInstance(data),
      this.mapIssue(data),
      this.mapCalls(data),
      this.mapOperations(data),
      this.mapLocations(data),
      this.mapHardCodedStringLocations(data)
    );
  }

  private mapInstance(data: any): CsvPayload {
    const instance = data.instance;

    // Setup the file with columns that are always present
    const headers = ['Instance ID', 'Number', 'Severity', 'Filter status', 'Data'];
    const content: string[] = [this.gqlIdEncoder.decode(instance.id), instance.numberLocal, instance.severity, instance.filterStatus, instance.data];

    // Only add columns for optional fields if they are available
    const { remediation, triage, specificRemediationAdvice } = instance.remediation?.general ?? {};
    [
      ['Remediation', remediation],
      ['Triage', triage],
      ['Specific remediation advice', specificRemediationAdvice],
    ]
      .filter(([, field]) => field)
      .forEach(([title, field]) => {
        headers.push(title);
        content.push(field);
      });

    return new CsvPayload(headers, [content]);
  }

  private mapIssue(data: any): CsvPayload {
    const issue = data.report.rules.edges[0].node;

    // Setup the file with columns that are always present
    const headers = ['Issue ID', 'Number', 'Title', 'Severity'];
    const content: string[] = [this.gqlIdEncoder.decode(issue.id), issue.number, issue.title, issue.severity];

    // Only add columns for optional fields if they are available
    const { shortExplanation, explanation, consequences, access, expertise, resources } = issue;
    [
      ['Summary', shortExplanation],
      ['Explanation', explanation],
      ['Consequences', consequences],
      ['Access', access],
      ['Expertise', expertise],
      ['Resources', resources],
    ]
      .filter(([, field]) => field)
      .forEach(([title, field]) => {
        headers.push(title);
        content.push(field);
      });

    return new CsvPayload(headers, [content]);
  }

  private mapCalls(data: any): CsvPayload {
    const calls = data.instance?.calls?.edges ?? [];
    const headers = ['Call ID', 'Timestamp', 'Kept by filters', 'Arguments'];
    const content: string[][] = calls.map(({ node }: any) => [this.gqlIdEncoder.decode(node.id), node.timestamp, node.isKeptByFilters, node.arguments]);

    return new CsvPayload(headers, content);
  }

  private mapOperations(data: any): CsvPayload {
    const headers = ['Operation ID', 'Type', 'Category', 'Context', 'Algorithms'];
    const content: string[][] = data.instance.operations.edges.map(({ node }: any) => [
      this.gqlIdEncoder.decode(node.id),
      getOperationTypeLabel(node.type),
      getOperationCategoryLabel(node.category),
      getOperationContextLabel(node.context),
      node.algorithms.join(', '),
    ]);

    return new CsvPayload(headers, content);
  }

  private mapLocations(data: any): CsvPayload {
    const locations = data.instance?.locations?.edges ?? [];
    const headers = ['Location ID', 'Hostname', 'File type', 'Path'];
    const content: string[][] = locations.map(({ node }: any) => [this.gqlIdEncoder.decode(node.id), node.hostname, node.fileType, node.path]);

    return new CsvPayload(headers, content);
  }

  private mapHardCodedStringLocations(data: any): CsvPayload {
    const headers = ['Class', 'Method', 'File', 'Line'];
    const content: string[][] = data.instance.hardCodedStringLocations?.map((node: any) => [node.class, node.method, node.file, node.line]) ?? [];

    return new CsvPayload(headers, content);
  }
}
